import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useStoreState } from 'easy-peasy';
import { useAuthContext } from "contexts/AuthContext"

import { httpGetEmails, httpGetEmail, httpMarkEmailProcessed, httpMarkEmailUnprocessed } from "services/emails" 

import { Row, Col, Container, Table, Collapse, Card as BootstrapCard, CardBody } from "reactstrap";
import { Input } from "stories/forms";
import { Card, Typography, Button, Link, IconButton, Modal, Spinner, BreadcrumbsDynamic } from "stories/components";
import { PageHeader, PageContainer } from "components/Page"
import { Pagination } from "stories/tables"

import EmailFilesList from "./EmailFilesList"
import "./ReceivedEmails.css"

const ReceivedEmails = ({match, history, location}) => {
  
  const { t } = useTranslation();
  const { myUser } = useAuthContext()
  const myAccount = useStoreState((state) => state.account?.account);

  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [count, setCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({}); // Track loading state per email
  
  const [filters, setFilters] = useState({
    limit: 30,
    offset: 0,
    ordering: '-id', // Vanhimmat ensin
    search: '',
    user_processed: 'false', // Oletuksena vain käsittelemättömät
    has_files: '',
    processing_status: '',
  })
  
  const getCrumbLinks = () => {
    const crumbLinks = [
      {name: t("Etusivu"), url: "/dashboard/"},
      {name: t("routes_dashboard_transactions_collapse", "Tapahtumat"), url: "/dashboard/transactions"},
      {name: t("routes_dashboard_received_emails", "Saapuneet sähköpostit"), url: "/dashboard/received-emails"},
    ]
    return crumbLinks
  }
  
  const getEmails = useCallback(() => {
    setLoading(true);
    
    // Build params, exclude empty filters
    const params = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        params[key] = filters[key];
      }
    });
    
    httpGetEmails(params).then(res => {
      console.log("Email API response:", res);
      console.log("Email data:", res?.data);
      
      // Check if data is paginated (has results) or direct array
      const emailData = res?.data?.results ? res?.data?.results : (Array.isArray(res?.data) ? res?.data : []);
      const totalCount = res?.data?.count || emailData.length || 0;
      
      setEmails(emailData);
      setCount(totalCount);
      setLoading(false);
    }, error => {
      console.error("Error fetching emails:", error);
      setLoading(false);
    });
  }, [filters]);
  
  useEffect(() => {
    getEmails();
  }, [filters]);
  
  const handleChange = ({target}) => {
    setFilters({
      ...filters,
      [target.name]: target.value,
    })
  }
  
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
      offset: 0, // Reset to first page when filtering
    })
  }
  
  const paginationPrevious = () => {
    let offset = filters?.offset - filters?.limit;
    if (offset < 0) {
      offset = 0;
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const paginationNext = () => {
    let offset = filters?.offset + filters?.limit;
    if (offset > count) {
      return
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const getEmailDetail = useCallback((emailId, forceRefresh = false) => {
    // Check if detail data already loaded or currently loading
    if (loadingDetails[emailId] && !forceRefresh) {
      return; // Already loading
    }
    
    const email = emails.find(e => e.id === emailId);
    // Check if detail data already loaded (has files array with detailed data)
    if (!forceRefresh && email && email.files !== undefined && Array.isArray(email.files)) {
      // files array exists, detail data already loaded
      return;
    }
    
    setLoadingDetails(prev => ({ ...prev, [emailId]: true }));
    
    // Use detail endpoint: /api/emails/123/
    httpGetEmail(emailId).then(res => {
      console.log("Email detail API response:", res);
      
      // Get the detailed email data
      const detailData = res?.data;
      
      if (detailData) {
        // Update the specific email in the emails array
        setEmails(prevEmails => 
          prevEmails.map(email => 
            email.id === emailId ? { ...email, ...detailData } : email
          )
        );
      }
      
      setLoadingDetails(prev => ({ ...prev, [emailId]: false }));
    }, error => {
      console.error("Error fetching email detail:", error);
      setLoadingDetails(prev => ({ ...prev, [emailId]: false }));
    });
  }, [emails, loadingDetails]);
  
  const toggleRow = (emailId) => {
    const isCurrentlyExpanded = expandedRows[emailId];
    
    setExpandedRows(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
    
    // If opening the row, fetch detail data
    if (!isCurrentlyExpanded) {
      getEmailDetail(emailId);
    }
  }
  
  const handleMarkProcessed = (emailId, currentStatus) => {
    if (currentStatus) {
      httpMarkEmailUnprocessed(emailId).then(() => {
        getEmails();
      });
    } else {
      httpMarkEmailProcessed(emailId).then(() => {
        getEmails();
      });
    }
  }
  
  const getProcessingStatusBadge = (status) => {
    const statusConfig = {
      'no_files': { color: 'secondary', text: t('Ei tiedostoja') },
      'unprocessed': { color: 'warning', text: t('Käsittelemättä') },
      'partially_processed': { color: 'info', text: t('Osittain käsitelty') },
      'ai_processed': { className: 'badge badge-ai-processed', text: t('AI käsitelty') },
      'fully_reviewed': { color: 'success', text: t('Tarkistettu') },
      'has_errors': { color: 'danger', text: t('Virheitä') },
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    const className = config.className || `badge badge-${config.color}`;
    return <span className={className}>{config.text}</span>;
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('DD.MM.YYYY HH:mm');
  }
  
  const renderEmailRow = (email, index) => {
    const isExpanded = expandedRows[email.id];
    const hasFiles = email.files && email.files.length > 0;
    
    // Calculate processed/total pre-records
    const totalProcessed = (email.total_processed_pre_transactions || 0) + (email.total_processed_pre_options || 0);
    const totalUnprocessed = (email.total_unprocessed_pre_transactions || 0) + (email.total_unprocessed_pre_options || 0);
    const totalRecords = totalProcessed + totalUnprocessed;
    const recordsText = totalRecords > 0 ? `${totalProcessed}/${totalRecords}` : '-';
    
    return (
      <React.Fragment key={email.id}>
        <tr 
          className="cursor-pointer"
          onClick={() => toggleRow(email.id)}
          style={{cursor: 'pointer'}}
        >
          <td>
            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} mr-2`}></i>
          </td>
          <td>{formatDate(email.received_at)}</td>
          <td>{email.sender}</td>
          <td className="received-emails-subject-col" title={email.subject || undefined}>
            {email.subject}
          </td>
          <td className="text-center">{email.total_files || 0}</td>
          <td className="text-center">{recordsText}</td>
          <td>{getProcessingStatusBadge(email.processing_status)}</td>
          <td className="text-center">
            {email.total_processing_errors > 0 && (
              <span className="badge badge-danger">{email.total_processing_errors}</span>
            )}
          </td>
          <td>
            <Button
              size="sm"
              color={email.user_processed ? "success" : "secondary"}
              className={!email.user_processed ? "btn-action" : undefined}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkProcessed(email.id, email.user_processed);
              }}
            >
              <i className={`fas fa-envelope${email.user_processed ? '-open-text' : ''} mr-1`}></i>
              {email.user_processed ? t('Käsitelty') : t('mark_email_processed', 'Merkitse käsitellyksi')}
            </Button>
          </td>
        </tr>
        {isExpanded && (
          <tr>
            <td colSpan="9" className="p-0">
              <div className="p-4" style={{ backgroundColor: '#F6F9FC' }}>
                {loadingDetails[email.id] ? (
                  <div className="text-center p-5">
                    <Spinner />
                    <div className="mt-2">{t('Ladataan tiedostoja...')}</div>
                  </div>
                ) : (
                  <EmailFilesList 
                    email={email} 
                    onRefresh={() => getEmailDetail(email.id, true)} 
                  />
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }
  
  return (
    <>
      <PageHeader title={t("routes_dashboard_received_emails", "Saapuneet sähköpostit")} />
      <BreadcrumbsDynamic links={getCrumbLinks()} />
      
      <PageContainer>
        {/* Filters */}
        <BootstrapCard className="mb-3 received-emails-filters">
          <CardBody>
            <Row>
              <Col xs="12" md="6" xl="3" className="mb-3 mb-xl-0">
                <label className="mb-1 header-bold d-block">{t('Haku')}</label>
                <Input
                  type="text"
                  name="search"
                  placeholder={t('Hae lähettäjä, aihe...')}
                  value={filters.search}
                  onChange={handleChange}
                />
              </Col>
              <Col xs="12" md="6" xl="2" className="mb-3 mb-xl-0">
                <label className="mb-1 header-bold d-block">{t('Käsittely')}</label>
                <Input
                  type="select"
                  name="user_processed"
                  value={filters.user_processed}
                  onChange={handleChange}
                >
                  <option value="">{t('Kaikki')}</option>
                  <option value="false">{t('Käsittelemättä')}</option>
                  <option value="true">{t('Käsitelty')}</option>
                </Input>
              </Col>
              <Col xs="12" md="6" xl="2" className="mb-3 mb-xl-0">
                <label className="mb-1 header-bold d-block">{t('Tiedostot')}</label>
                <Input
                  type="select"
                  name="has_files"
                  value={filters.has_files}
                  onChange={handleChange}
                >
                  <option value="">{t('Kaikki')}</option>
                  <option value="true">{t('Sisältää tiedostoja')}</option>
                  <option value="false">{t('Ei tiedostoja')}</option>
                </Input>
              </Col>
              <Col xs="12" md="6" xl="3" className="mb-3 mb-xl-0">
                <label className="mb-1 header-bold d-block">{t('Käsittelytila')}</label>
                <Input
                  type="select"
                  name="processing_status"
                  value={filters.processing_status}
                  onChange={handleChange}
                >
                  <option value="">{t('Kaikki')}</option>
                  <option value="no_files">{t('Ei tiedostoja')}</option>
                  <option value="unprocessed">{t('Käsittelemättä')}</option>
                  <option value="partially_processed">{t('Osittain käsitelty')}</option>
                  <option value="ai_processed">{t('AI käsitelty')}</option>
                  <option value="fully_reviewed">{t('Tarkistettu')}</option>
                  <option value="has_errors">{t('Virheitä')}</option>
                </Input>
              </Col>
              <Col xs="12" md="6" xl="2" className="d-flex align-items-end">
                <Button
                  className="sk-quick-actions__btn"
                  variant="secondary"
                  onClick={() => setFilters({
                    limit: 30,
                    offset: 0,
                    ordering: '-id', // Vanhimmat ensin
                    search: '',
                    user_processed: 'false', // Palautetaan oletukseen: käsittelemättömät
                    has_files: '',
                    processing_status: '',
                  })}
                >
                  {t('Tyhjennä')}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </BootstrapCard>
        
        {/* Email Table */}
        {loading ? (
          <div className="text-center p-5">
            <Spinner />
          </div>
        ) : (
          <>
            <Table responsive className="table-flush received-emails-table" hover>
              <thead className="thead-light">
                <tr>
                  <th style={{width: '30px'}}></th>
                  <th>{t('Vastaanotettu')}</th>
                  <th>{t('Lähettäjä')}</th>
                  <th className="received-emails-subject-col">{t('Aihe')}</th>
                  <th className="text-center">{t('Tiedostot')}</th>
                  <th className="text-center">{t('Kirjaukset')}</th>
                  <th>{t('Käsittelytila')}</th>
                  <th className="text-center">{t('Virheet')}</th>
                  <th>{t('Toiminnot')}</th>
                </tr>
              </thead>
              <tbody>
                {emails && emails.length > 0 ? (
                  emails.map((email, index) => renderEmailRow(email, index))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      {t('Ei tuloksia')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            
            {/* Pagination */}
            {!loading && (
              <Pagination 
                paginationPrevious={paginationPrevious}
                paginationNext={paginationNext}
                paginationOffset={filters?.offset}
                paginationLimit={filters?.limit}
                paginationCount={count}
                setFilters={setFilters}
                showPaginationLimitSelect={true}
                showPaginationNextAndPrevious={true}
                showPaginationCurrentPage={true}
                showVerticalBorders={true}
                name="limit"
                limitOptions={[
                  { value: 10, label: '10' },
                  { value: 30, label: '30' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 10000, label: t('Kaikki') }
                ]}
              />
            )}
          </>
        )}
      </PageContainer>
    </>
  );
}

export default ReceivedEmails;

